import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const DAY_OPTIONS = [7, 14, 30, 60];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const monthIndex = (date) => (date.getFullYear() * 12) + date.getMonth();

const firstOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const toYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const toDisplayDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const toCompactCurrency = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
};

const toEps = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return value.toFixed(2);
};

const getEventSymbol = (event) => String(event?.symbol || event?.ticker || '').trim().toUpperCase();

const getEventCompany = (event) => String(event?.companyName || event?.company || event?.name || '').trim();

const EarningsCalendarTab = ({ onSelectTicker, defaultExchange = 'NASDAQ' }) => {
  const navigate = useNavigate();
  const [daysAhead, setDaysAhead] = useState(14);
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('both');
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => firstOfMonth(new Date()));

  useEffect(() => {
    const controller = new AbortController();

    const loadCalendar = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/earnings/calendar?days=${daysAhead}&limit=2000`,
          { signal: controller.signal }
        );

        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body?.error || `HTTP ${response.status}`);
        }

        setPayload(body);
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Failed to load earnings calendar');
      } finally {
        setLoading(false);
      }
    };

    loadCalendar();

    return () => controller.abort();
  }, [daysAhead]);

  const events = useMemo(() => {
    const source = Array.isArray(payload?.events) ? payload.events : [];
    const term = searchText.trim().toLowerCase();
    if (!term) return source;

    return source.filter((event) => {
      const symbol = getEventSymbol(event).toLowerCase();
      const companyName = getEventCompany(event).toLowerCase();
      return symbol.includes(term) || companyName.includes(term);
    });
  }, [payload, searchText]);

  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const event of events) {
      const dateKey = String(event?.date || '').slice(0, 10);
      if (!dateKey) continue;
      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey).push(event);
    }
    return map;
  }, [events]);

  const monthBounds = useMemo(() => {
    const dateKeys = Array.from(eventsByDate.keys()).sort();
    if (!dateKeys.length) return null;

    const minDate = new Date(`${dateKeys[0]}T00:00:00`);
    const maxDate = new Date(`${dateKeys[dateKeys.length - 1]}T00:00:00`);
    if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) return null;

    return {
      min: firstOfMonth(minDate),
      max: firstOfMonth(maxDate),
    };
  }, [eventsByDate]);

  useEffect(() => {
    if (!monthBounds) {
      return;
    }

    setCalendarMonth((currentMonth) => {
      const currentIdx = monthIndex(currentMonth);
      const minIdx = monthIndex(monthBounds.min);
      const maxIdx = monthIndex(monthBounds.max);

      if (currentIdx < minIdx) return monthBounds.min;
      if (currentIdx > maxIdx) return monthBounds.max;
      return currentMonth;
    });
  }, [monthBounds]);

  const calendarCells = useMemo(() => {
    const start = firstOfMonth(calendarMonth);
    const startDay = start.getDay();
    const totalDays = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();

    const cells = [];
    const totalGridCells = 42;

    for (let i = 0; i < totalGridCells; i += 1) {
      const dayNumber = i - startDay + 1;
      const inCurrentMonth = dayNumber >= 1 && dayNumber <= totalDays;

      if (!inCurrentMonth) {
        cells.push({ key: `empty-${i}`, inCurrentMonth: false, dayNumber: null, dateKey: null, events: [] });
        continue;
      }

      const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), dayNumber);
      const dateKey = toYmd(date);

      cells.push({
        key: dateKey,
        inCurrentMonth: true,
        dayNumber,
        dateKey,
        events: eventsByDate.get(dateKey) || [],
      });
    }

    return cells;
  }, [calendarMonth, eventsByDate]);

  useEffect(() => {
    if (!selectedDateKey) return;
    if (!eventsByDate.has(selectedDateKey)) {
      setSelectedDateKey('');
    }
  }, [eventsByDate, selectedDateKey]);

  const tableEvents = useMemo(() => {
    if (!selectedDateKey) return events;
    return eventsByDate.get(selectedDateKey) || [];
  }, [events, eventsByDate, selectedDateKey]);

  const handleTickerClick = (symbol) => {
    const normalizedTicker = String(symbol || '').trim().toUpperCase();
    if (!normalizedTicker) return;
    if (typeof onSelectTicker === 'function') {
      onSelectTicker(normalizedTicker, defaultExchange || 'NASDAQ');
    }
    navigate('/dashboard');
  };

  const canGoPrevMonth = monthBounds
    ? monthIndex(calendarMonth) > monthIndex(monthBounds.min)
    : false;

  const canGoNextMonth = monthBounds
    ? monthIndex(calendarMonth) < monthIndex(monthBounds.max)
    : false;

  const calendarMonthLabel = calendarMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const moveCalendarMonth = (delta) => {
    setCalendarMonth((current) => firstOfMonth(new Date(current.getFullYear(), current.getMonth() + delta, 1)));
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
            Earnings Calendar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Upcoming company earnings report calls and announcement windows.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_event, nextValue) => {
              if (nextValue !== null) setViewMode(nextValue);
            }}
          >
            <ToggleButton value="calendar">Calendar</ToggleButton>
            <ToggleButton value="table">Table</ToggleButton>
            <ToggleButton value="both">Both</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={daysAhead}
            onChange={(_event, nextValue) => {
              if (nextValue !== null) setDaysAhead(nextValue);
            }}
          >
            {DAY_OPTIONS.map((days) => (
              <ToggleButton key={days} value={days}>{days}D</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            size="small"
            label="Search symbol or company"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </Stack>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
          <CircularProgress size={20} />
          <Typography>Loading earnings calendar...</Typography>
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {!loading && !error && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip size="small" label={`Window: ${payload?.from || '-'} to ${payload?.to || '-'}`} />
            <Chip size="small" color="primary" label={`${events.length} events`} />
          </Stack>

          {(viewMode === 'calendar' || viewMode === 'both') && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Visual Calendar
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  size="small"
                  onClick={() => moveCalendarMonth(-1)}
                  disabled={!canGoPrevMonth}
                  aria-label="Previous month"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 130, textAlign: 'center', fontWeight: 600 }}>
                  {calendarMonthLabel}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => moveCalendarMonth(1)}
                  disabled={!canGoNextMonth}
                  aria-label="Next month"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1, mb: 1 }}>
              {WEEKDAY_LABELS.map((label) => (
                <Typography
                  key={label}
                  variant="caption"
                  color="text.secondary"
                  sx={{ textAlign: 'center', fontWeight: 700 }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1 }}>
              {calendarCells.map((cell) => (
                <Paper
                  key={cell.key}
                  variant="outlined"
                  onClick={() => {
                    if (!cell.inCurrentMonth || !cell.dateKey) return;
                    setSelectedDateKey((current) => (current === cell.dateKey ? '' : cell.dateKey));
                  }}
                  sx={{
                    p: 0.75,
                    minHeight: { xs: 88, sm: 106 },
                    opacity: cell.inCurrentMonth ? 1 : 0.35,
                    bgcolor: cell.inCurrentMonth ? 'background.paper' : 'action.hover',
                    cursor: cell.inCurrentMonth ? 'pointer' : 'default',
                    borderColor: selectedDateKey === cell.dateKey ? 'primary.main' : undefined,
                    borderWidth: selectedDateKey === cell.dateKey ? 2 : 1,
                  }}
                >
                  {cell.inCurrentMonth && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {cell.dayNumber}
                      </Typography>
                      <Stack spacing={0.4} sx={{ mt: 0.6 }}>
                        {cell.events.slice(0, 3).map((event) => {
                          const symbol = getEventSymbol(event);
                          return (
                            <Chip
                              key={`${cell.dateKey}-${symbol}-${event.time || 'na'}`}
                              size="small"
                              color="primary"
                              label={symbol || '-'}
                              onClick={() => handleTickerClick(symbol)}
                              sx={{ height: 20, justifyContent: 'flex-start' }}
                            />
                          );
                        })}
                        {cell.events.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            +{cell.events.length - 3} more
                          </Typography>
                        )}
                      </Stack>
                    </>
                  )}
                </Paper>
              ))}
            </Box>
          </Paper>
          )}

          {(viewMode === 'table' || viewMode === 'both') && (
          <Paper variant="outlined">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  {selectedDateKey ? 'Showing tickers for selected date:' : 'Showing all dates'}
                </Typography>
                {selectedDateKey && (
                  <Chip size="small" color="primary" label={toDisplayDate(selectedDateKey)} />
                )}
              </Stack>
              {selectedDateKey && (
                <Chip
                  size="small"
                  label="Clear date filter"
                  onClick={() => setSelectedDateKey('')}
                  variant="outlined"
                />
              )}
            </Stack>
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Ticker</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell align="right">EPS Est.</TableCell>
                    <TableCell align="right">EPS Actual</TableCell>
                    <TableCell align="right">Revenue Est.</TableCell>
                    <TableCell align="right">Revenue Actual</TableCell>
                    <TableCell>Fiscal End</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableEvents.map((event) => {
                    const symbol = getEventSymbol(event);
                    const companyName = getEventCompany(event);
                    return (
                    <TableRow key={`${symbol || 'na'}-${event.date}-${event.time || 'na'}`} hover>
                      <TableCell>{toDisplayDate(event.date)}</TableCell>
                      <TableCell>{event.time || 'TBD'}</TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => handleTickerClick(symbol)}
                          sx={{ fontWeight: 700, cursor: 'pointer' }}
                        >
                          {symbol || '-'}
                        </Link>
                      </TableCell>
                      <TableCell>{companyName || '-'}</TableCell>
                      <TableCell align="right">{toEps(event.epsEstimated ?? event.epsEstimate ?? null)}</TableCell>
                      <TableCell align="right">{toEps(event.epsActual)}</TableCell>
                      <TableCell align="right">{toCompactCurrency(event.revenueEstimated ?? event.revenueEstimate ?? null)}</TableCell>
                      <TableCell align="right">{toCompactCurrency(event.revenueActual)}</TableCell>
                      <TableCell>{(event.fiscalDateEnding || event.fiscalEnd) ? toDisplayDate(event.fiscalDateEnding || event.fiscalEnd) : '-'}</TableCell>
                    </TableRow>
                  );
                  })}
                  {tableEvents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                          {selectedDateKey
                            ? 'No earnings events found for the selected date.'
                            : 'No earnings events found for this range.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default EarningsCalendarTab;
